import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TablaIcon from './TablaIcon';

const adminNav = [
  { path:'/dashboard',  label:'Dashboard',     icon:'▣' },
  { path:'/students',   label:'Students',       icon:'◎' },
  { path:'/classes',    label:'Classes',        icon:'◈' },
  { path:'/lessons',    label:'Lessons',        icon:'◆' },
  { path:'/homework',   label:'Homework',       icon:'◉' },
  { path:'/progress',   label:'Progress',       icon:'▲' },
  { path:'/media',      label:'Media Library',  icon:'▷' },
  { path:'/schedule',   label:'Schedule',       icon:'◷' },
  { path:'/approvals',  label:'Approvals',      icon:'👥' },
  { path:'/payments',   label:'Payments',       icon:'💳' },
  { path:'/settings',   label:'Taals & Types',  icon:'◈' },
  { path:'/export',     label:'Export Data',    icon:'↓' },
];

const studentNav = [
  { path:'/dashboard',   label:'Dashboard',   icon:'▣' },
  { path:'/classes',     label:'My Classes',  icon:'◈' },
  { path:'/lessons',     label:'My Lessons',  icon:'◆' },
  { path:'/homework',    label:'My Homework', icon:'◉' },
  { path:'/progress',    label:'My Progress', icon:'▲' },
  { path:'/media',       label:'My Media',    icon:'▷' },
  { path:'/schedule',    label:'My Schedule', icon:'◷' },
  { path:'/my-payments', label:'Payments',    icon:'💳' },
];

export default function Sidebar({ mobileOpen, onClose, isMobile }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const navItems = isAdmin ? adminNav : studentNav;

  const sidebarStyle = {
    ...s.sidebar,
    transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    transition: 'transform 0.25s ease',
    boxShadow: isMobile && mobileOpen ? '4px 0 24px rgba(0,0,0,0.6)' : 'none',
  };

  return (
    <>
      {isMobile && mobileOpen && <div style={s.overlay} onClick={onClose}/>}
      <aside style={sidebarStyle}>
        <div style={s.brand}>
          <div style={s.iconWrap}><TablaIcon size={28} color="#c49a3c"/></div>
          <div>
            <div style={s.brandName}>RhythMicky</div>
            <div style={s.brandSub}>Beats Institute</div>
          </div>
          {isMobile && (
            <button onClick={onClose} style={s.closeBtn}>✕</button>
          )}
        </div>

        <div style={s.userBlock}>
          <div style={s.avatar}>{(user?.username?.[0] || 'U').toUpperCase()}</div>
          <div style={s.userInfo}>
            <div style={s.userName}>
              {user?.student?.full_name || (user?.username?.charAt(0).toUpperCase() + user?.username?.slice(1))}
            </div>
            <div style={s.userRole}>{isAdmin ? 'Teacher' : 'Student'}</div>
          </div>
        </div>

        <div style={s.divider}/>

        <nav style={s.nav}>
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} onClick={onClose}
              style={({ isActive }) => isActive ? { ...s.item, ...s.itemActive } : s.item}>
              <span style={s.glyph}>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={{ flex:1 }}/>
        <div style={s.divider}/>
        <button style={s.signOut} onClick={() => { logout(); navigate('/'); }}>
          <span style={s.glyph}>→</span><span>Sign Out</span>
        </button>
      </aside>
    </>
  );
}

const s = {
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:99, backdropFilter:'blur(2px)' },
  sidebar: { width:'256px', minHeight:'100vh', background:'#111110', display:'flex', flexDirection:'column', padding:'0 0 16px', position:'fixed', left:0, top:0, bottom:0, zIndex:100, borderRight:'1px solid #2a2927', overflowY:'auto' },
  brand: { display:'flex', alignItems:'center', gap:'12px', padding:'20px 16px', borderBottom:'1px solid #2a2927', flexShrink:0 },
  iconWrap: { width:'40px', height:'40px', background:'rgba(196,154,60,0.1)', border:'1px solid rgba(196,154,60,0.2)', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  brandName: { fontSize:'14px', fontWeight:600, color:'#f0ede8', letterSpacing:'-0.2px' },
  brandSub: { fontSize:'11px', color:'#6b6966', marginTop:'2px' },
  closeBtn: { marginLeft:'auto', background:'none', border:'none', color:'#6b6966', fontSize:'18px', cursor:'pointer', padding:'4px 6px', borderRadius:'6px', flexShrink:0 },
  userBlock: { display:'flex', alignItems:'center', gap:'10px', padding:'11px 12px', margin:'12px 10px 0', background:'#1c1b19', borderRadius:'8px', border:'1px solid #2a2927', flexShrink:0 },
  avatar: { width:'30px', height:'30px', borderRadius:'6px', background:'rgba(196,154,60,0.15)', border:'1px solid rgba(196,154,60,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:600, color:'#c49a3c', flexShrink:0 },
  userInfo: { flex:1, minWidth:0 },
  userName: { fontSize:'13px', fontWeight:500, color:'#e8e6e3', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  userRole: { fontSize:'11px', color:'#6b6966', marginTop:'1px' },
  divider: { height:'1px', background:'#2a2927', margin:'10px 0', flexShrink:0 },
  nav: { display:'flex', flexDirection:'column', padding:'0 10px', gap:'1px' },
  item: { display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'8px', textDecoration:'none', color:'#c8c5be', fontSize:'13px', fontWeight:400 },
  itemActive: { background:'#1c1b19', color:'#f0ede8', fontWeight:500, borderLeft:'2px solid #c49a3c', paddingLeft:'10px' },
  glyph: { fontSize:'13px', width:'18px', textAlign:'center', flexShrink:0, fontFamily:'monospace' },
  signOut: { display:'flex', alignItems:'center', gap:'10px', margin:'4px 10px 0', padding:'9px 12px', borderRadius:'8px', background:'none', border:'none', color:'#6b6966', fontSize:'13px', cursor:'pointer', flexShrink:0 },
};
